import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'

export type ChartRegistryLookup =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow }

// Loud-failure replacement for the silent `charts.data?.find(...) ?? null` +
// `if (!row) return null` recipe (Batch B F1 pattern). Consumers MUST visibly
// handle the missing case via JSX so a chart_registry row gap can't silently
// disappear a tile.
export function findChartRegistryRow(
  charts: UseQueryResult<ChartRegistryRow[], Error>,
  walkFn: string,
): ChartRegistryLookup {
  if (charts.isLoading) return { status: 'loading' }
  if (charts.error) return { status: 'error', error: charts.error }
  if (!charts.data) return { status: 'loading' }
  // Exclude summary_bar_strip, metric_driver_bars, and dynamics_panels rows: all three tile types
  // share walk functions with canvas tiles (summary_bar_strip shares walk_timeline;
  // metric_driver_bars shares walk_ratio_absolute/walk_absolute; dynamics_panels shares
  // walk_timeline/walk_ratio) but are resolved separately by their own tile components.
  // Without this exclusion an excluded row could sort first and thread the wrong
  // default_facet/params into the timeline or absolute_bar tile.
  const row = charts.data.find(
    (r) =>
      r.walk_function === walkFn &&
      r.chart_type !== 'summary_bar_strip' &&
      r.chart_type !== 'metric_driver_bars' &&
      r.chart_type !== 'dynamics_panels',
  )
  if (!row) return { status: 'missing', walkFn }
  return { status: 'ok', row }
}

// A chart tile of a given kind is backed by EITHER the conventional walk function
// (walk_timeline / walk_absolute) OR — for ratio kpis (Sales RoI/CpO/OpC/Mediashare)
// — the ratio variant (walk_ratio / walk_ratio_absolute). Both produce identical
// row shapes; only the RPC dispatch differs. This resolves which variant the active
// chart_registry row carries so the caller can fire the matching walk hook.
export type WalkVariant = 'standard' | 'ratio'

export type ChartRegistryKindLookup =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow; variant: WalkVariant }

// Try the standard walk function first, then the ratio variant. A 'missing' result
// is reported against the standard function name (the primary expectation) so the
// loud-failure Alert stays meaningful. 'loading'/'error' short-circuit identically
// to findChartRegistryRow.
export function findChartRegistryRowForKind(
  charts: UseQueryResult<ChartRegistryRow[], Error>,
  standardFn: string,
  ratioFn: string,
): ChartRegistryKindLookup {
  const standard = findChartRegistryRow(charts, standardFn)
  if (standard.status === 'ok') return { ...standard, variant: 'standard' }
  if (standard.status === 'loading' || standard.status === 'error') return standard
  // standard is 'missing' — try the ratio variant before giving up.
  const ratio = findChartRegistryRow(charts, ratioFn)
  if (ratio.status === 'ok') return { ...ratio, variant: 'ratio' }
  return { status: 'missing', walkFn: standardFn }
}
